import * as actionTypes from "./bookingActionType";
import axios from 'axios';
import { checkError, apiTimeout } from './../error';
import { toast } from 'react-toastify';
import { restoreCustomerSession } from '../customer/customerAction';
import {
    customerAuthHeaders,
    extractPaymentCustomer,
    persistPaymentConfirmation,
    readPaymentConfirmation,
    isBookingConfirmedLocally,
    isBookingConfirmInFlight,
    lockBookingConfirmInFlight,
    unlockBookingConfirmInFlight,
    markBookingConfirmedLocally,
} from '../../utils/customerSession';
const { REACT_APP_API_URL } = process.env;

export const BOOKING_DRAFT_STORAGE_KEY = 'jadecaps_request_booking_draft';
export const GUEST_BOOKING_INFO_STORAGE_KEY = 'jadecaps_request_booking_guest';
export const REQUEST_RECEIVED_STORAGE_KEY = 'jadecaps_request_received';

/** Guest checkout fields persist across visits; booking draft stays tab-scoped. */
const guestBookingStorage = () => localStorage;

const readGuestBookingInfoRaw = () => {
    try {
        const store = guestBookingStorage();
        let raw = store.getItem(GUEST_BOOKING_INFO_STORAGE_KEY);
        if (!raw) {
            const legacy = sessionStorage.getItem(GUEST_BOOKING_INFO_STORAGE_KEY);
            if (legacy) {
                store.setItem(GUEST_BOOKING_INFO_STORAGE_KEY, legacy);
                sessionStorage.removeItem(GUEST_BOOKING_INFO_STORAGE_KEY);
                raw = legacy;
            }
        }
        return raw;
    } catch {
        return null;
    }
};

export const readBookingDraft = () => {
    try {
        const raw = sessionStorage.getItem(BOOKING_DRAFT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const writeBookingDraft = (data) => {
    try {
        if (data?.id && data?.checkinDate && data?.checkoutDate) {
            sessionStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, JSON.stringify(data));
        }
    } catch {
        // ignore quota / private mode errors
    }
};

export const writeRequestReceivedSnapshot = (data) => {
    try {
        if (!data) return;
        sessionStorage.setItem(REQUEST_RECEIVED_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // ignore
    }
};

export const readRequestReceivedSnapshot = () => {
    try {
        const raw = sessionStorage.getItem(REQUEST_RECEIVED_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const clearRequestReceivedSnapshot = () => {
    try {
        sessionStorage.removeItem(REQUEST_RECEIVED_STORAGE_KEY);
    } catch {
        // ignore
    }
};

export const emptyGuestBookingInfo = () => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequest: '',
    addGstDetails: false,
    buyerGstin: '',
    buyerLegalName: '',
    buyerBillingAddress: '',
});

export const readGuestBookingInfo = (propertyId) => {
    try {
        const raw = readGuestBookingInfoRaw();
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (propertyId != null && String(parsed?.propertyId) !== String(propertyId)) {
            return null;
        }
        return {
            ...emptyGuestBookingInfo(),
            ...parsed,
            addGstDetails: Boolean(parsed?.addGstDetails),
        };
    } catch {
        return null;
    }
};

export const loadGuestBookingInfoForCheckout = (routePropertyId) => {
    const draft = readBookingDraft();
    const propertyId = routePropertyId || draft?.id || null;
    const stored = readGuestBookingInfo(propertyId);
    if (stored) {
        return {
            propertyId,
            user: {
                firstName: stored.firstName || '',
                lastName: stored.lastName || '',
                email: stored.email || '',
                phone: stored.phone || '',
                specialRequest: stored.specialRequest || '',
                addGstDetails: Boolean(stored.addGstDetails),
                buyerGstin: stored.buyerGstin || '',
                buyerLegalName: stored.buyerLegalName || '',
                buyerBillingAddress: stored.buyerBillingAddress || '',
            },
        };
    }
    return { propertyId, user: emptyGuestBookingInfo() };
};

export const writeGuestBookingInfo = (propertyId, guest) => {
    try {
        if (propertyId == null || !guest) return;
        guestBookingStorage().setItem(
            GUEST_BOOKING_INFO_STORAGE_KEY,
            JSON.stringify({
                propertyId: String(propertyId),
                firstName: guest.firstName || '',
                lastName: guest.lastName || '',
                email: guest.email || '',
                phone: guest.phone || '',
                specialRequest: guest.specialRequest || '',
                addGstDetails: Boolean(guest.addGstDetails),
                buyerGstin: guest.buyerGstin || '',
                buyerLegalName: guest.buyerLegalName || '',
                buyerBillingAddress: guest.buyerBillingAddress || '',
            })
        );
    } catch {
        // ignore quota / private mode errors
    }
};

export const clearGuestBookingInfo = () => {
    try {
        guestBookingStorage().removeItem(GUEST_BOOKING_INFO_STORAGE_KEY);
        sessionStorage.removeItem(GUEST_BOOKING_INFO_STORAGE_KEY);
    } catch {
        // ignore
    }
};

export const clearBookingDraft = () => {
    try {
        sessionStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
        clearGuestBookingInfo();
    } catch {
        // ignore
    }
};

/** Calendar date → `YYYY-MM-DDT00:00:00.000Z` for booking API payloads */
export const toUtcMidnightIso = (value) => {
    if (value == null || value === '') return value;

    if (typeof value === 'string') {
        const day = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (day) return `${day[1]}T00:00:00.000Z`;
    }

    if (value?.isValid && typeof value.toISODate === 'function') {
        const day = value.toISODate();
        if (day) return `${day}T00:00:00.000Z`;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T00:00:00.000Z`;
    }

    return value;
};

const withBookingDates = (data) => {
    if (!data || typeof data !== 'object') return data;
    const next = { ...data };
    if ('checkinDate' in next) next.checkinDate = toUtcMidnightIso(next.checkinDate);
    if ('checkoutDate' in next) next.checkoutDate = toUtcMidnightIso(next.checkoutDate);
    return next;
};

/** Calendar `/booking/prices` expects `YYYY-MM-DD` query params */
const withBookingDateParams = (data) => {
    if (!data || typeof data !== 'object') return data;
    const next = { ...data };
    if ('checkinDate' in next) {
        const iso = toUtcMidnightIso(next.checkinDate);
        next.checkinDate = typeof iso === 'string' ? iso.slice(0, 10) : iso;
    }
    if ('checkoutDate' in next) {
        const iso = toUtcMidnightIso(next.checkoutDate);
        next.checkoutDate = typeof iso === 'string' ? iso.slice(0, 10) : iso;
    }
    return next;
};

/** Public resolve for short admin checkout links (`/c/:slug`). */
export const fetchCheckoutLinkBySlug = (slug) => {
    const token = String(slug || '').trim();
    if (!token) {
        return Promise.reject(new Error('Checkout link not found'));
    }
    return apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/checkout-link/${encodeURIComponent(token)}`))
        .then((response) => response.data?.data || response.data)
        .catch((error) => {
            checkError(error);
            throw error;
        });
};

/** Public resolve for a guest payment-request link (`/pay/:slug` — security deposit etc). */
export const fetchPaymentRequestBySlug = (slug) => {
    const token = String(slug || '').trim();
    if (!token) {
        return Promise.reject(new Error('Payment link not found'));
    }
    return apiTimeout(axios.get(`${REACT_APP_API_URL}/payment-request/${encodeURIComponent(token)}`))
        .then((response) => response.data?.data || response.data)
        .catch((error) => {
            checkError(error);
            throw error;
        });
};

/** Public confirm for a guest payment-request link, after Razorpay Checkout succeeds. */
export const confirmPaymentRequest = (slug, payload) => {
    const token = String(slug || '').trim();
    if (!token) {
        return Promise.reject(new Error('Payment link not found'));
    }
    return apiTimeout(axios.post(`${REACT_APP_API_URL}/payment-request/${encodeURIComponent(token)}/confirm`, payload))
        .then((response) => response.data?.data || response.data)
        .catch((error) => {
            checkError(error);
            throw error;
        });
};

/** Public — poll after Razorpay / page refresh (status only). */
export const fetchBookingPaymentStatus = (bookingId) => {
    const id = Number(bookingId);
    if (!Number.isFinite(id) || id < 1) {
        return Promise.reject(new Error('Invalid booking id'));
    }
    return apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/${id}/payment-status`))
        .then((response) => response.data?.data || response.data)
        .catch((error) => {
            throw error;
        });
};

export const fetchBookingQuote = (params) =>
    apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/quote`, {
        ...customerAuthHeaders(),
        params,
    })).then((response) => response.data?.data || response.data);

export const createBooking = (data) => {
    const payload = withBookingDates(data);
    return (dispatch) => {
    dispatch(createBookingInit());
    return apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/store`, payload, customerAuthHeaders()))
    .then((response) => {
        dispatch(createBookingSuccess(response.data));
        return response.data;
    })
    .catch(function (error) {
        dispatch(createBookingFailure());
        checkError(error);
        throw error;
    });
    };
};

/** Request to Book — no payment; creates status=5 enquiry */
export const createRequestToBook = (data) => {
    const payload = withBookingDates(data);
    return (dispatch) => {
        dispatch(createBookingInit());
        return apiTimeout(
            axios.post(`${REACT_APP_API_URL}/booking/request`, payload, customerAuthHeaders())
        )
            .then((response) => {
                dispatch(createBookingSuccess(response.data));
                return response.data;
            })
            .catch(function (error) {
                dispatch(createBookingFailure());
                checkError(error);
                throw error;
            });
    };
};

/** Public thank-you page — load request summary by id */
export const fetchPublicRequestToBook = (requestId) =>
    apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/request/${requestId}`)).then(
        (response) => response.data?.data
    );

export const createBookingInit = () => ({
    type: actionTypes.CREATE_BOOKING
});

export const createBookingFailure = () => ({
    type: actionTypes.CREATE_BOOKING_FAILURE
});

export const createBookingSuccess = data => ({
    type: actionTypes.CREATE_BOOKING_SUCCESS,
    payload: { data }
});

export const createBookingHDFC = (data) => {
    const payload = withBookingDates(data);
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` } };
    return (dispatch) => {
    dispatch(createBookingHDFCInit());
    apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/store-hdfc`, payload, config))
    .then((response) => {
        dispatch(createBookingHDFCSuccess(response.data.data));
    })
    .catch(function (error) {
        dispatch(createBookingHDFCFailure());
        checkError(error);
    })
    };
};

export const createBookingHDFCInit = () => ({
    type: actionTypes.CREATE_BOOKING_HDFC
});

export const createBookingHDFCFailure = () => ({
    type: actionTypes.CREATE_BOOKING_HDFC_FAILURE
});

export const createBookingHDFCSuccess = data => ({
    type: actionTypes.CREATE_BOOKING_HDFC_SUCCESS,
    payload: { data }
});

export const retryBookingHDFC = (data) => {
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` } };
    return (dispatch) => {
    dispatch(retryBookingHDFCInit());
    apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/retry-payment`, data, config))
    .then((response) => {
        dispatch(retryBookingHDFCSuccess(response.data.data));
    })
    .catch(function (error) {
        dispatch(retryBookingHDFCFailure());
        checkError(error);
    })
    };
};

export const retryBookingHDFCInit = () => ({
    type: actionTypes.CREATE_BOOKING_HDFC
});

export const retryBookingHDFCFailure = () => ({
    type: actionTypes.CREATE_BOOKING_HDFC_FAILURE
});

export const retryBookingHDFCSuccess = data => ({
    type: actionTypes.CREATE_BOOKING_HDFC_SUCCESS,
    payload: { data }
});

export const cancelBooking = (data) => {
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` } };
    return (dispatch) => {
    dispatch(cancelBookingInit());
    return apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/cancel-booking`, data, config))
    .then((response) => {
        dispatch(cancelBookingSuccess(response.data));
        return response.data;
    })
    .catch(function (error) {
        dispatch(cancelBookingFailure());
        checkError(error);
        throw error;
    })
    };
};

export const cancelBookingInit = () => ({
    type: actionTypes.CANCEL_BOOKING
});

export const cancelBookingFailure = () => ({
    type: actionTypes.CANCEL_BOOKING_FAILURE
});

export const cancelBookingSuccess = data => ({
    type: actionTypes.CANCEL_BOOKING_SUCCESS,
    payload: { data }
});

export const applyCoupon = (data) => {
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` } };
    const silent = Boolean(data?.silent);
    const payload = { ...data };
    delete payload.silent;
    return (dispatch) => {
    dispatch(applyCouponInit());
    return apiTimeout(axios.post(`${REACT_APP_API_URL}/coupon/apply-coupon`, payload, config))
    .then((response) => {
        if (!silent) {
            toast.success(`${payload.code} applied.`, {
                position: toast.POSITION.TOP_RIGHT,
                theme: "colored"
            });
        }
        dispatch(applyCouponSuccess(response.data));
        return response.data;
    })
    .catch(function (error) {
        dispatch(applyCouponFailure());
        if (silent) {
            // Revalidation failed (ineligible after date/property/cart change) — drop coupon quietly
            dispatch(removeCouponInit());
        } else if (error?.response?.status === 400 && error.response.data?.message) {
            // Coupon rules come back with a plain reason and what to change, e.g.
            // "This offer needs a booking of at least ₹12,000. Add ₹2,400 more to unlock it."
            const { message, data } = error.response.data;
            toast.error(data?.hint ? `${message} ${data.hint}` : message, {
                position: toast.POSITION.TOP_RIGHT,
                theme: "colored"
            });
        } else {
            checkError(error);
        }
        throw error;
    })
    };
};

export const applyCouponInit = () => ({
    type: actionTypes.APPLY_COUPON
});

export const applyCouponFailure = () => ({
    type: actionTypes.APPLY_COUPON_FAILURE
});

export const applyCouponSuccess = data => ({
    type: actionTypes.APPLY_COUPON_SUCCESS,
    payload: { data }
});

export const removeCoupon = () => {
    return (dispatch) => {
        dispatch(removeCouponInit());
    };
};

export const removeCouponInit = () => ({
    type: actionTypes.REMOVE_COUPON
});

export const getEligibleCoupons = (params) => (dispatch) => {
    dispatch({ type: actionTypes.GET_ELIGIBLE_COUPONS });
    const query = new URLSearchParams({
        propertyId: params.propertyId,
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        amount: params.amount,
        nights: params.nights,
    }).toString();
    apiTimeout(axios.get(`${REACT_APP_API_URL}/coupon/eligible?${query}`))
        .then((response) => dispatch({ type: actionTypes.GET_ELIGIBLE_COUPONS_SUCCESS, payload: response.data }))
        .catch(() => dispatch({ type: actionTypes.GET_ELIGIBLE_COUPONS_FAILURE }));
};

export const getEligibleComplimentaryAddOns = (params) => (dispatch) => {
    dispatch({ type: actionTypes.GET_ELIGIBLE_COMPLIMENTARY_ADDONS });
    const query = new URLSearchParams({
        propertyId: params.propertyId,
        checkIn: String(params.checkIn || '').slice(0, 10),
        baseStayPrice: params.baseStayPrice,
        ...(params.baseStayPriceAfterDiscount != null
            ? { baseStayPriceAfterDiscount: params.baseStayPriceAfterDiscount }
            : {}),
    }).toString();
    apiTimeout(axios.get(`${REACT_APP_API_URL}/complimentary-addon/eligible?${query}`))
        .then((response) =>
            dispatch({
                type: actionTypes.GET_ELIGIBLE_COMPLIMENTARY_ADDONS_SUCCESS,
                payload: response.data,
            })
        )
        .catch(() =>
            dispatch({ type: actionTypes.GET_ELIGIBLE_COMPLIMENTARY_ADDONS_FAILURE })
        );
};

export const clearEligibleComplimentaryAddOns = () => ({
    type: actionTypes.CLEAR_ELIGIBLE_COMPLIMENTARY_ADDONS,
});

function isConfirmPaymentSuccessful(body) {
    if (!body || typeof body !== 'object') return false;
    if (body.success === false) return false;
    if (body.status && body.status !== 'success') return false;
    return true;
}

export const resetBookingCheckoutState = () => ({
    type: actionTypes.RESET_BOOKING_CHECKOUT,
});

export const confirmBooking = (data) => (dispatch, getState) => {
    const bookingId = data?.id;
    if (bookingId && isBookingConfirmedLocally(bookingId)) {
        const snap = readPaymentConfirmation();
        return Promise.resolve(snap?.booking ? { status: 'success', booking: snap.booking } : { status: 'success' });
    }
    if (bookingId && isBookingConfirmInFlight(bookingId)) {
        return Promise.reject(Object.assign(new Error('Payment confirmation is already in progress.'), { confirmInFlight: true }));
    }
    if (bookingId) {
        lockBookingConfirmInFlight(bookingId);
    }
    dispatch(confirmBookingInit());
    return apiTimeout(
        axios.post(`${REACT_APP_API_URL}/booking/confirm-payment`, data, customerAuthHeaders())
    )
        .then((response) => {
            const body = response.data;
            if (!isConfirmPaymentSuccessful(body)) {
                const err = new Error(
                    body?.message || 'Payment received but the booking could not be confirmed. Please contact support.'
                );
                err.confirmFailed = true;
                throw err;
            }
            const { booking: stateBooking, savedProperty } = getState().bookingPage;
            const customer = extractPaymentCustomer(body);
            if (customer) {
                dispatch(restoreCustomerSession(customer));
            }
            persistPaymentConfirmation(body, stateBooking, savedProperty, data?.paymentId);
            if (bookingId) {
                markBookingConfirmedLocally(bookingId);
                unlockBookingConfirmInFlight(bookingId);
            }
            dispatch(confirmBookingSuccess(body));
            return body;
        })
        .catch(function (error) {
            if (bookingId) {
                unlockBookingConfirmInFlight(bookingId);
            }
            dispatch(confirmBookingFailure());
            if (!error?.confirmFailed && !error?.confirmInFlight) {
                checkError(error);
            }
            throw error;
        });
};

export const confirmBookingInit = () => ({
    type: actionTypes.CONFIRM_BOOKING
});

export const confirmBookingFailure = () => ({
    type: actionTypes.CONFIRM_BOOKING_FAILURE
});

export const confirmBookingSuccess = (data) => ({
    type: actionTypes.CONFIRM_BOOKING_SUCCESS,
    payload: { data }
});

export const checkBooking = (data) => {
    const payload = withBookingDates(data);
    return (dispatch) => {
    dispatch(checkBookingInit());
    apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/check-booking`, payload))
    .then((response) => {
        dispatch(checkBookingSuccess(response.data));
    })
    .catch(function (error) {
        dispatch(checkBookingFailure());
        checkError(error);
    })
    };
};

/**
 * Promise-based availability check for guest flows.
 * Returns { available: true } or { available: false, message }.
 * Does not toast — caller decides how to inform the guest.
 */
export const checkBookingAvailability = (data) => () => {
    const payload = withBookingDates(data);
    return apiTimeout(axios.post(`${REACT_APP_API_URL}/booking/check-booking`, payload))
        .then(() => ({ available: true }))
        .catch((error) => ({
            available: false,
            message:
                error?.response?.data?.message ||
                error?.message ||
                'These dates are no longer available.',
        }));
};

export const checkBookingInit = () => ({
    type: actionTypes.CHECK_BOOKING
});

export const checkBookingFailure = () => ({
    type: actionTypes.CHECK_BOOKING_FAILURE
});

export const checkBookingSuccess = () => ({
    type: actionTypes.CHECK_BOOKING_SUCCESS
});

export const getPrices = (data) => {
    const params = withBookingDateParams(data);
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` }, params };
    return (dispatch) => {
    dispatch(getPricesInit());
    apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/prices`, config))
    .then((response) => {
        dispatch(getPricesSuccess(response.data.data));
    })
    .catch(function (error) {
        dispatch(getPricesFailure());
        checkError(error);
    })
    };
};

export const getPricesInit = () => ({
    type: actionTypes.GET_PRICES
});

export const getPricesFailure = () => ({
    type: actionTypes.GET_PRICES_FAILURE
});

export const getPricesSuccess = (data) => ({
    type: actionTypes.GET_PRICES_SUCCESS,
    payload: { data }
});

export const fetchBooking = (data) => {
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem("customerToken")}` }, params: data };
    return (dispatch) => {
    dispatch(fetchBookingInit());
    apiTimeout(axios.get(`${REACT_APP_API_URL}/booking/consumer`, config))
    .then((response) => {
        dispatch(fetchBookingSuccess(response.data.data));
    })
    .catch(function (error) {
        dispatch(fetchBookingFailure());
        checkError(error);
    })
    };
};

/** Create a fresh Razorpay order for a pending (status=1) guest booking */
export const resumeConsumerPayment = (bookingId) => () =>
    apiTimeout(
        axios.post(
            `${REACT_APP_API_URL}/booking/consumer/resume-payment`,
            { id: bookingId },
            customerAuthHeaders()
        )
    ).then((response) => response.data?.data);

export const hydrateBookingFromPaymentSnapshot = () => (dispatch) => {
    const snap = readPaymentConfirmation();
    if (snap?.booking) {
        dispatch(fetchBookingByIdSuccess(snap.booking));
    }
};

export const fetchBookingById = (bookingId, { silentAuthError = false } = {}) => (dispatch) => {
    const snap = readPaymentConfirmation();
    const snapshotBooking =
        snap?.booking && String(snap.booking.id) === String(bookingId) ? snap.booking : null;

    if (snapshotBooking) {
        dispatch(fetchBookingByIdSuccess(snapshotBooking));
    }

    if (!customerAuthHeaders().headers) {
        return Promise.resolve(snapshotBooking);
    }

    dispatch(fetchBookingByIdInit(!!snapshotBooking));
    return apiTimeout(
        axios.get(`${REACT_APP_API_URL}/booking/consumer/${bookingId}`, customerAuthHeaders())
    )
        .then((response) => {
            dispatch(fetchBookingByIdSuccess(response.data.data));
            return response.data.data;
        })
        .catch(function (error) {
            dispatch(fetchBookingByIdFailure());
            if (silentAuthError && error?.response?.status === 401) {
                return snap?.booking ?? null;
            }
            checkError(error);
            throw error;
        });
};

export const fetchBookingInit = () => ({
    type: actionTypes.FETCH_BOOKING
});

export const fetchBookingFailure = () => ({
    type: actionTypes.FETCH_BOOKING_FAILURE
});

export const fetchBookingSuccess = data => ({
    type: actionTypes.FETCH_BOOKING_SUCCESS,
    payload: { data }
});

export const fetchBookingByIdInit = (keepExistingBooking = false) => ({
    type: actionTypes.FETCH_BOOKING_BY_ID,
    payload: { keepExistingBooking },
});

export const fetchBookingByIdFailure = () => ({
    type: actionTypes.FETCH_BOOKING_BY_ID_FAILURE
});

export const fetchBookingByIdSuccess = data => ({
    type: actionTypes.FETCH_BOOKING_BY_ID_SUCCESS,
    payload: { data }
});

export const savePropertyForNext = (data) => {
    return (dispatch) => {
        writeBookingDraft(data);
        dispatch(savePropertyForNextSuccess(data));
    }
};

export const savePropertyForNextSuccess = (data) => ({
    type: actionTypes.SAVE_PROPERTY_FOR_NEXT,
    payload: { data }
});

export const removePropertyForNext = () => {
    return (dispatch) => {
        clearBookingDraft();
        dispatch(removePropertyForNextSuccess());
    }
};

export const removePropertyForNextSuccess = () => ({
    type: actionTypes.REMOVE_PROPERTY_FOR_NEXT
});